<template>
  <div class="excel-bulk-fixer">
    <div class="flex">
      <sidebar></sidebar>
      <div class="flex-1 p-6">
        <div class="bg-white rounded-lg shadow-sm p-6">
          <h1 class="text-2xl font-bold mb-6">Excel ile Toplu Veri Düzeltme</h1>

          <!-- Erişim kontrolü -->
          <div v-if="!hasAccess" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            Bu sayfaya erişim yetkiniz bulunmamaktadır.
          </div>

          <div v-else>
            <!-- Bilgilendirme -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 class="font-semibold text-blue-800 mb-2">Nasıl Kullanılır?</h3>
              <ol class="list-decimal list-inside text-blue-700 space-y-1 text-sm">
                <li>Aşağıdan Excel şablonunu indirin</li>
                <li><strong>A kolonuna (phone)</strong> düzeltilecek lead'in telefon numarasını yazın</li>
                <li><strong>B kolonundan itibaren</strong> güncellemek istediğiniz alanların başlıklarını yazın (örn: aciklama, name, email)</li>
                <li>Her satırda telefon numarası ve güncellenecek değerleri girin</li>
                <li>Dosyayı yükleyin ve önizlemeyi kontrol edin</li>
                <li>Onaylayıp kaydedin</li>
              </ol>
              <p class="text-orange-600 mt-2 text-sm font-medium">
                <strong>Limit:</strong> Maksimum 50.000 kayıt işlenebilir.
              </p>
            </div>

            <!-- Şablon İndirme -->
            <div class="mb-6">
              <h3 class="font-semibold mb-3">1. Şablon İndir</h3>
              <div class="flex items-center gap-4">
                <select v-model="selectedFields" multiple class="border rounded p-2 h-32 w-64">
                  <option v-for="field in availableFields" :key="field.name" :value="field.name">
                    {{ field.display_name || field.name }}
                  </option>
                </select>
                <div class="text-sm text-gray-500">
                  <p>Güncellemek istediğiniz alanları seçin</p>
                  <p>(Ctrl+Click ile çoklu seçim yapabilirsiniz)</p>
                </div>
                <button
                  @click="downloadTemplate"
                  class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                  :disabled="selectedFields.length === 0"
                >
                  <vk-icon icon="download" ratio="0.8"></vk-icon>
                  Şablonu İndir
                </button>
              </div>
            </div>

            <!-- Dosya Yükleme -->
            <div class="mb-6">
              <h3 class="font-semibold mb-3">2. Excel Dosyası Yükle</h3>
              <div
                class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
                @click="$refs.fileInput.click()"
                @drop.prevent="handleDrop"
                @dragover.prevent
              >
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileSelect"
                  accept=".xlsx,.xls,.csv"
                  class="hidden"
                />
                <vk-icon icon="cloud-upload" ratio="2" class="text-gray-400 mb-2"></vk-icon>
                <p class="text-gray-600">
                  {{ fileName || 'Dosya seçmek için tıklayın veya sürükleyip bırakın' }}
                </p>
                <p class="text-sm text-gray-400 mt-1">(.xlsx, .xls, .csv)</p>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-8">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p class="mt-4 text-gray-600">{{ loadingMessage }}</p>
            </div>

            <!-- Önizleme -->
            <div v-if="previewData.length > 0 && !loading" class="mb-6">
              <h3 class="font-semibold mb-3">3. Önizleme</h3>

              <!-- Özet Bilgiler -->
              <div class="grid grid-cols-4 gap-4 mb-4">
                <div class="bg-blue-50 p-3 rounded">
                  <p class="text-sm text-blue-600">Toplam Kayıt</p>
                  <p class="text-2xl font-bold text-blue-800">{{ previewData.length }}</p>
                </div>
                <div class="bg-green-50 p-3 rounded">
                  <p class="text-sm text-green-600">Bulunan</p>
                  <p class="text-2xl font-bold text-green-800">{{ foundCount }}</p>
                </div>
                <div class="bg-red-50 p-3 rounded">
                  <p class="text-sm text-red-600">Bulunamayan</p>
                  <p class="text-2xl font-bold text-red-800">{{ notFoundCount }}</p>
                </div>
                <div class="bg-yellow-50 p-3 rounded">
                  <p class="text-sm text-yellow-600">Güncellenecek Alan</p>
                  <p class="text-2xl font-bold text-yellow-800">{{ updateColumns.length }}</p>
                </div>
              </div>

              <!-- Güncellenecek alanlar -->
              <div class="mb-4">
                <p class="text-sm text-gray-600 mb-2">
                  <strong>Güncellenecek alanlar:</strong> {{ updateColumns.join(', ') }}
                </p>
              </div>

              <!-- Tablo -->
              <div class="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50 sticky top-0">
                    <tr>
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefon</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lead ID</th>
                      <th
                        v-for="col in updateColumns"
                        :key="col"
                        class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {{ getFieldDisplayName(col) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(row, index) in previewData.slice(0, 100)"
                      :key="index"
                      :class="{ 'bg-red-50': !row.found, 'bg-green-50': row.found }"
                    >
                      <td class="px-4 py-2 whitespace-nowrap">
                        <span
                          class="px-2 py-1 text-xs rounded-full"
                          :class="row.found ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'"
                        >
                          {{ row.found ? 'Bulundu' : 'Bulunamadı' }}
                        </span>
                      </td>
                      <td class="px-4 py-2 whitespace-nowrap text-sm">{{ row.phone }}</td>
                      <td class="px-4 py-2 whitespace-nowrap text-sm">{{ row.lead_id || '-' }}</td>
                      <td
                        v-for="col in updateColumns"
                        :key="col"
                        class="px-4 py-2 text-sm max-w-xs truncate"
                        :title="row.data[col]"
                      >
                        {{ row.data[col] || '-' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-if="previewData.length > 100" class="text-sm text-gray-500 mt-2">
                İlk 100 kayıt gösteriliyor. Toplam {{ previewData.length }} kayıt işlenecek.
              </p>

              <!-- Kaydet Butonu -->
              <div class="mt-6 flex gap-4">
                <button
                  @click="saveChanges"
                  class="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
                  :disabled="foundCount === 0 || saving"
                >
                  <vk-icon icon="check" ratio="0.8"></vk-icon>
                  {{ saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet' }}
                </button>
                <button
                  @click="clearData"
                  class="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300"
                >
                  Temizle
                </button>
              </div>
            </div>

            <!-- Sonuç -->
            <div v-if="result" class="mt-6">
              <div
                class="p-4 rounded-lg"
                :class="result.success ? 'bg-green-100 border border-green-400' : 'bg-red-100 border border-red-400'"
              >
                <h3 class="font-semibold" :class="result.success ? 'text-green-800' : 'text-red-800'">
                  {{ result.success ? 'İşlem Başarılı!' : 'Hata Oluştu' }}
                </h3>
                <p :class="result.success ? 'text-green-700' : 'text-red-700'">
                  {{ result.message }}
                </p>
                <div v-if="result.details" class="mt-2 text-sm">
                  <p>Güncellenen: {{ result.details.updated }}</p>
                  <p>Atlanan: {{ result.details.skipped }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from "../components/Ui/Admin/Sidebar.vue";
import http from "../services/http";
import * as XLSX from "xlsx";

export default {
  name: "ExcelBulkFixer",
  components: { Sidebar },
  data() {
    return {
      hasAccess: false,
      allowedDepartments: ["admin", "admin1", "admin2"],
      availableFields: [],
      selectedFields: [],
      fileName: "",
      fileData: null,
      previewData: [],
      updateColumns: [],
      loading: false,
      loadingMessage: "",
      saving: false,
      result: null,
      maxRecords: 50000,
    };
  },
  computed: {
    foundCount() {
      return this.previewData.filter((r) => r.found).length;
    },
    notFoundCount() {
      return this.previewData.filter((r) => !r.found).length;
    },
  },
  methods: {
    checkAccess() {
      const cache = this.$root.cache || {};
      const me = cache.me || JSON.parse(localStorage.getItem("me") || "{}");
      this.hasAccess = this.allowedDepartments.includes(me.department);
    },
    async loadFields() {
      try {
        const response = await http.fields("leads");
        this.availableFields = response.data.filter(
          (f) => f.name !== "id" && f.name !== "created_at" && f.name !== "updated_at"
        );
      } catch (error) {
        console.error("Fields yüklenemedi:", error);
      }
    },
    getFieldDisplayName(fieldName) {
      const field = this.availableFields.find((f) => f.name === fieldName);
      return field ? field.display_name || field.name : fieldName;
    },
    downloadTemplate() {
      const headers = ["phone", ...this.selectedFields];
      const exampleRow = ["905551234567", ...this.selectedFields.map(() => "örnek değer")];

      const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Lead Düzeltme");

      // Kolon genişlikleri
      ws["!cols"] = headers.map(() => ({ wch: 20 }));

      XLSX.writeFile(wb, "lead_duzeltme_sablonu.xlsx");
    },
    handleDrop(e) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    async processFile(file) {
      this.fileName = file.name;
      this.loading = true;
      this.loadingMessage = "Dosya okunuyor...";
      this.result = null;
      this.previewData = [];

      try {
        const data = await this.readExcelFile(file);

        if (data.length === 0) {
          throw new Error("Dosyada veri bulunamadı");
        }

        if (data.length > this.maxRecords) {
          throw new Error(`Maksimum ${this.maxRecords} kayıt yüklenebilir. Dosyada ${data.length} kayıt var.`);
        }

        // İlk satır başlıklar
        const headers = data[0];
        const phoneIndex = headers.findIndex(
          (h) => h && h.toString().toLowerCase().trim() === "phone"
        );

        if (phoneIndex === -1) {
          throw new Error("'phone' kolonu bulunamadı. İlk kolonda 'phone' başlığı olmalıdır.");
        }

        // Güncellenecek kolonları bul
        this.updateColumns = headers
          .filter((h, i) => i !== phoneIndex && h && h.toString().trim())
          .map((h) => h.toString().trim().toLowerCase());

        if (this.updateColumns.length === 0) {
          throw new Error("Güncellenecek alan bulunamadı. Lütfen en az bir alan ekleyin.");
        }

        // Verileri işle
        this.loadingMessage = "Telefon numaraları kontrol ediliyor...";
        const rows = data.slice(1).filter((row) => row[phoneIndex]);

        // Telefonları normalize et ve lead'leri bul
        const phones = rows.map((row) => this.normalizePhone(row[phoneIndex]));
        const leadMap = await this.findLeadsByPhones(phones);

        // Preview data oluştur
        this.previewData = rows.map((row) => {
          const phone = this.normalizePhone(row[phoneIndex]);
          const leadInfo = leadMap[phone];
          const rowData = {};

          headers.forEach((header, index) => {
            if (index !== phoneIndex && header) {
              const key = header.toString().trim().toLowerCase();
              rowData[key] = row[index] !== undefined ? row[index].toString() : "";
            }
          });

          return {
            phone,
            found: !!leadInfo,
            lead_id: leadInfo ? leadInfo.id : null,
            data: rowData,
          };
        });

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.result = {
          success: false,
          message: error.message,
        };
      }
    },
    readExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
            resolve(jsonData);
          } catch (err) {
            reject(new Error("Dosya okunamadı: " + err.message));
          }
        };
        reader.onerror = () => reject(new Error("Dosya okunamadı"));
        reader.readAsArrayBuffer(file);
      });
    },
    normalizePhone(phone) {
      if (!phone) return "";
      return phone.toString().replace(/\D/g, "");
    },
    async findLeadsByPhones(phones) {
      try {
        const response = await http.bulkFindLeadsByPhone({ phones });
        return response.data || {};
      } catch (error) {
        console.error("Lead arama hatası:", error);
        return {};
      }
    },
    async saveChanges() {
      if (this.foundCount === 0) return;

      this.saving = true;
      this.result = null;

      try {
        const updates = this.previewData
          .filter((row) => row.found && row.lead_id)
          .map((row) => ({
            id: row.lead_id,
            data: row.data,
          }));

        const response = await http.bulkUpdateLeads({
          updates,
          columns: this.updateColumns,
        });

        this.result = {
          success: true,
          message: `${response.data.updated} kayıt başarıyla güncellendi.`,
          details: response.data,
        };

        this.previewData = [];
        this.fileName = "";
      } catch (error) {
        this.result = {
          success: false,
          message: (error.response && error.response.data && error.response.data.message) || error.message || "Bir hata oluştu",
        };
      } finally {
        this.saving = false;
      }
    },
    clearData() {
      this.previewData = [];
      this.updateColumns = [];
      this.fileName = "";
      this.fileData = null;
      this.result = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
  },
  mounted() {
    this.checkAccess();
    if (this.hasAccess) {
      this.loadFields();
    }
  },
};
</script>

<style scoped>
.excel-bulk-fixer {
  min-height: 100vh;
  background-color: #f5f5f5;
}
</style>
